import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Pill } from "@/components/perf";
import { toast } from "sonner";
import { Plus, Trash2, Save, CreditCard } from "lucide-react";
import { TIER_ICONS, TierIcon } from "@/components/tierIcons";

const EMPTY = { name: "", price: 0, price_coins: "", recurring: true, perks: "", access: "free", icon: "star", monthly_coins: 0, monthly_tournaments: 0, monthly_sessions: 0, monthly_videos: 0 };
const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red";
const lbl = "block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5";

const IconPicker = ({ value, onChange, testid }) => (
  <div data-testid={testid} className="flex flex-wrap gap-1.5">
    {Object.entries(TIER_ICONS).map(([key, Icon]) => (
      <button key={key} type="button" title={key} data-testid={`${testid}-${key}`} onClick={() => onChange(key)}
        className={`w-9 h-9 flex items-center justify-center border transition-all ${value === key ? "border-pb-red bg-pb-red/15 text-pb-red" : "border-pb-border text-zinc-500 hover:text-white hover:border-zinc-500"}`}>
        <Icon className="w-4 h-4" />
      </button>
    ))}
  </div>
);

function TierEditor({ tier, onSave, onDelete, busy }) {
  const [f, setF] = useState({ name: tier.name, price: tier.price, price_coins: tier.price_coins ?? "", recurring: tier.recurring, perks: (tier.perks || []).join("\n"), access: tier.access, icon: tier.icon || "star", monthly_coins: tier.monthly_coins || 0, monthly_tournaments: tier.monthly_tournaments || 0, monthly_sessions: tier.monthly_sessions || 0, monthly_videos: tier.monthly_videos || 0 });
  return (
    <div className="border border-pb-border bg-pb-elevated/40 p-4" data-testid={`tier-card-${tier.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TierIcon icon={f.icon} className="w-4 h-4 text-pb-red" />
          <span className="font-display font-black uppercase">{tier.name}</span>
          {tier.core ? <Pill color="#FACC15">Core</Pill> : <Pill color="#22D3EE">Custom</Pill>}
        </div>
        {tier.id !== "free" && (
          <button data-testid={`tier-delete-${tier.id}`} onClick={() => onDelete(tier)} disabled={busy}
            className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mb-3">
        <div className="sm:col-span-2"><label className={lbl}>Card Name</label>
          <input data-testid={`tier-name-${tier.id}`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Price ($/mo)</label>
          <input data-testid={`tier-price-${tier.id}`} type="number" min={0} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Coin Price (🪙)</label>
          <input data-testid={`tier-coins-${tier.id}`} type="number" min={0} placeholder="—" value={f.price_coins} onChange={(e) => setF({ ...f, price_coins: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Feature Access</label>
          {tier.id === "free" ? (
            <div className="px-3 py-2 text-sm text-zinc-500 border border-pb-border/60 uppercase">{tier.access} (locked)</div>
          ) : (
            <select data-testid={`tier-access-${tier.id}`} value={f.access} onChange={(e) => setF({ ...f, access: e.target.value })} className={input}>
              <option value="free">Free features</option>
              <option value="vip">VIP features</option>
              <option value="ace">ACE features</option>
            </select>
          )}
        </div>
      </div>
      <div className="border border-[#A3E635]/40 bg-[#A3E635]/5 p-3 mb-3" data-testid={`tier-autoperks-${tier.id}`}>
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635] mb-0.5">Auto-Perks — the system delivers these itself, every month</div>
        <p className="text-[10px] text-zinc-500 mb-2">Whatever you enter is credited to every member on this tier automatically (on their first visit each month) and redeemed at checkout with zero work from you.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><label className={lbl}>Coins / mo (bonus 🪙)</label>
            <input data-testid={`tier-mcoins-${tier.id}`} type="number" min={0} value={f.monthly_coins} onChange={(e) => setF({ ...f, monthly_coins: e.target.value })} className={input} /></div>
          <div><label className={lbl}>Free tournaments / mo</label>
            <input data-testid={`tier-mtourn-${tier.id}`} type="number" min={0} value={f.monthly_tournaments} onChange={(e) => setF({ ...f, monthly_tournaments: e.target.value })} className={input} /></div>
          <div><label className={lbl}>Free sessions / mo</label>
            <input data-testid={`tier-msess-${tier.id}`} type="number" min={0} value={f.monthly_sessions} onChange={(e) => setF({ ...f, monthly_sessions: e.target.value })} className={input} /></div>
          <div><label className={lbl}>AI film credits / mo</label>
            <input data-testid={`tier-mvideo-${tier.id}`} type="number" min={0} value={f.monthly_videos} onChange={(e) => setF({ ...f, monthly_videos: e.target.value })} className={input} /></div>
        </div>
      </div>
      <div className="mb-3"><label className={lbl}>Card Icon (shows next to the name)</label>
        <IconPicker value={f.icon} onChange={(icon) => setF({ ...f, icon })} testid={`tier-icon-${tier.id}`} /></div>
      <div className="mb-3"><label className={lbl}>Perks shown on the card (one per line)</label>
        <textarea data-testid={`tier-perks-${tier.id}`} rows={4} value={f.perks} onChange={(e) => setF({ ...f, perks: e.target.value })} className={`${input} resize-none`} /></div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input type="checkbox" checked={f.recurring} onChange={(e) => setF({ ...f, recurring: e.target.checked })} className="accent-[#A3E635]" />
          Recurring monthly billing label
        </label>
        <button data-testid={`tier-save-${tier.id}`} disabled={busy}
          onClick={() => onSave(tier, { name: f.name, price: Number(f.price) || 0, price_coins: Number(f.price_coins) || null, recurring: f.recurring, access: f.access, icon: f.icon, perks: f.perks.split("\n").map((p) => p.trim()).filter(Boolean), monthly_coins: Number(f.monthly_coins) || 0, monthly_tournaments: Number(f.monthly_tournaments) || 0, monthly_sessions: Number(f.monthly_sessions) || 0, monthly_videos: Number(f.monthly_videos) || 0 })}
          className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
          <Save className="w-3 h-3" /> Save Card
        </button>
      </div>
    </div>
  );
}

export const MembershipCardsPanel = () => {
  const [tiers, setTiers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/membership-tiers"); setTiers(data); }
    catch { toast.error("Couldn't load membership cards"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (tier, payload) => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/membership-tiers/${tier.id}`, payload); setTiers(data); toast.success(`${payload.name} card updated — members see it immediately`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (tier) => {
    if (!window.confirm(`Delete the "${tier.name}" card? Members holding it keep their access level.`)) return;
    setBusy(true);
    try { const { data } = await api.delete(`/admin/membership-tiers/${tier.id}`); setTiers(data); toast.success("Card deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/membership-tiers", {
        name: nf.name, price: Number(nf.price) || 0, price_coins: Number(nf.price_coins) || null, recurring: nf.recurring, access: nf.access, icon: nf.icon,
        perks: nf.perks.split("\n").map((p) => p.trim()).filter(Boolean),
        monthly_coins: Number(nf.monthly_coins) || 0, monthly_tournaments: Number(nf.monthly_tournaments) || 0,
        monthly_sessions: Number(nf.monthly_sessions) || 0, monthly_videos: Number(nf.monthly_videos) || 0,
      });
      setTiers(data); setNf(EMPTY); setShowNew(false); toast.success("New membership card is live");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-pb-surface border border-pb-border p-5 mt-6" data-testid="membership-cards-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Membership Cards</span>
        </div>
        <button data-testid="new-tier-btn" onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-pb-cyan hover:text-black transition-all">
          <Plus className="w-3 h-3" /> {showNew ? "Cancel" : "Add New Card"}
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Everything here is exactly what members see on the membership cards — names, prices, perks and AUTO-PERKS the system fulfills by itself. Stripe charges the price set here. Only the Free card can't be deleted (it's where everyone else lands).</p>

      {showNew && (
        <div className="border border-pb-cyan/50 bg-pb-cyan/5 p-4 mb-4" data-testid="new-tier-form">
          <div className="grid sm:grid-cols-4 gap-3 mb-3">
            <div className="sm:col-span-2"><label className={lbl}>Card Name</label>
              <input data-testid="new-tier-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Family Plan" className={input} /></div>
            <div><label className={lbl}>Price ($/mo)</label>
              <input data-testid="new-tier-price" type="number" min={0} value={nf.price} onChange={(e) => setNf({ ...nf, price: e.target.value })} className={input} /></div>
            <div><label className={lbl}>Coin Price (🪙)</label>
              <input data-testid="new-tier-coins" type="number" min={0} placeholder="—" value={nf.price_coins} onChange={(e) => setNf({ ...nf, price_coins: e.target.value })} className={input} /></div>
            <div><label className={lbl}>Feature Access</label>
              <select data-testid="new-tier-access" value={nf.access} onChange={(e) => setNf({ ...nf, access: e.target.value })} className={input}>
                <option value="free">Free features</option>
                <option value="vip">VIP features</option>
                <option value="ace">ACE features</option>
              </select></div>
          </div>
          <div className="mb-3"><label className={lbl}>Card Icon</label>
            <IconPicker value={nf.icon} onChange={(icon) => setNf({ ...nf, icon })} testid="new-tier-icon" /></div>
          <div className="mb-3"><label className={lbl}>Perks (one per line)</label>
            <textarea data-testid="new-tier-perks" rows={3} value={nf.perks} onChange={(e) => setNf({ ...nf, perks: e.target.value })} className={`${input} resize-none`} /></div>
          <div className="border border-[#A3E635]/40 bg-[#A3E635]/5 p-3 mb-3">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635] mb-2">Auto-Perks (system-fulfilled monthly)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><label className={lbl}>Coins / mo</label>
                <input data-testid="new-tier-mcoins" type="number" min={0} value={nf.monthly_coins} onChange={(e) => setNf({ ...nf, monthly_coins: e.target.value })} className={input} /></div>
              <div><label className={lbl}>Tournaments / mo</label>
                <input data-testid="new-tier-mtourn" type="number" min={0} value={nf.monthly_tournaments} onChange={(e) => setNf({ ...nf, monthly_tournaments: e.target.value })} className={input} /></div>
              <div><label className={lbl}>Sessions / mo</label>
                <input data-testid="new-tier-msess" type="number" min={0} value={nf.monthly_sessions} onChange={(e) => setNf({ ...nf, monthly_sessions: e.target.value })} className={input} /></div>
              <div><label className={lbl}>Film credits / mo</label>
                <input data-testid="new-tier-mvideo" type="number" min={0} value={nf.monthly_videos} onChange={(e) => setNf({ ...nf, monthly_videos: e.target.value })} className={input} /></div>
            </div>
          </div>
          <button data-testid="new-tier-create" onClick={create} disabled={busy || !nf.name.trim()}
            className="bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {busy ? "Creating…" : "Create Card"}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {tiers.map((t) => <TierEditor key={`${t.id}-${t.name}-${t.price}`} tier={t} onSave={save} onDelete={remove} busy={busy} />)}
      </div>
    </div>
  );
};

export default MembershipCardsPanel;
